import { FC } from 'react';
import { NudosButton, NudosTextInput, NudosStaticAlertInComponent } from '../../../../components/NudosComponents';
import NudosDatePicker from '../../../../components/NudosComponents/NudosDatePicker';
import BordOneToneIcon from '../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import DoubleConfirmationModal from '../../../../components/DoubleConfirmationModal/DoubleConfirmationModal';
import {
  IProductDelivery,
  IproductReferenceDataInCountry,
  IShipmentData,
  SHIPMENT_STATUS_ID,
  TEMPORARY_STORAGE_REASON_TYPE_ID
} from '../../../../types/orders';
import ProductSelector from '../ProductSelector/ProductSelector';
import { IconReserveBox } from 'assets/DesignSystem/SVGComponents';
import { getReasonForStopWarehouse } from '../../../../utils/getReasonForStopWarehouse';
import { IShipmentStatusTransitionConfig, useShipmentStatusTransition } from './useShipmentStatusTransition';
import './ShipmentStatusTransitionModal.scss';

interface IShipmentStatusTransitionModalProps {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  transitionConfig: IShipmentStatusTransitionConfig;
  updateWithoutLoaders?: () => void;
  onTransitionConfirmed?: (shipments: IShipmentData[]) => void;
  orderId: string;
  organizationName?: string;
  receiverName?: string;
  references?: IproductReferenceDataInCountry[];
  shipmentData?: IShipmentData;
  products?: IProductDelivery[];
  selectedShipments?: IShipmentData[];
  listOfShipmentsAvailableToAdd?: IShipmentData[];
  warehouseStopReasons?: TEMPORARY_STORAGE_REASON_TYPE_ID[];
  optionalShipmentTitle?: { current: number; total: number };
}

const ShipmentStatusTransitionModal: FC<IShipmentStatusTransitionModalProps> = ({
  setShowModal,
  shipmentData,
  products,
  selectedShipments,
  listOfShipmentsAvailableToAdd,
  transitionConfig,
  updateWithoutLoaders,
  onTransitionConfirmed,
  orderId,
  organizationName,
  receiverName,
  references,
  warehouseStopReasons,
  optionalShipmentTitle
}: IShipmentStatusTransitionModalProps) => {
  const {
    loading,
    setListOfShipmentsToChangeStatus,
    errorList,
    setErrorList,
    setHasProductSelectorErrors,
    isLegacyMode,
    shipmentsToUse,
    isBulkAction,
    firstShipment,
    countryTimeZone,
    destinationName,
    fallbackFlagImageUrl,
    disableButton,
    handlePrimaryButtonClick,
    handleCloseClick,
    showDeliveryFinalConfirm,
    setShowDeliveryFinalConfirm,
    secondStepVerificationText,
    handleConfirmDeliveryFinal,
    requiresDeliveryDate,
    deliveryDate,
    setDeliveryDate,
    requiresTransitTrackingInfo,
    estimatedEntryDate,
    setEstimatedEntryDate,
    trackingNumber,
    setTrackingNumber,
    trackingLink,
    setTrackingLink
  } = useShipmentStatusTransition({
    setShowModal,
    transitionConfig,
    updateWithoutLoaders,
    onTransitionConfirmed,
    receiverName,
    shipmentData,
    products,
    selectedShipments,
    orderId,
    warehouseStopReasons
  });

  return (
    <div id="shipmentStatusTransitionModal">
      <div className="containerForm">
        <div className="floatClose" onClick={handleCloseClick}>
          <BordOneToneIcon variant="x" customWidth="16px" />
        </div>
        <div className="boxForm">
          <div className="titleModal">
            <div className="iconContainer">
              <IconReserveBox />
            </div>
            <div className="titleModalContent">
              <span>
                {requiresTransitTrackingInfo && warehouseStopReasons?.length
                  ? `${transitionConfig.modalTitle} por:`
                  : transitionConfig.modalTitle}
              </span>
              {requiresTransitTrackingInfo && warehouseStopReasons && warehouseStopReasons.length > 0 && (
                <div className="titleModalReasonTags">
                  {warehouseStopReasons.map(reasonId => (
                    <span key={`${reasonId}-${orderId}`} className="titleModalReasonTag">
                      {getReasonForStopWarehouse(reasonId)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="containerSubTitle">
            <span className="firstPartSubTitle">{`${organizationName || 'Nombre Empresa'} - Orden N°${orderId || 0}`}</span>
            {!isBulkAction && (
              <>
                <span>|</span>
                <span>{`Destino - ${destinationName}`}</span>
                <img
                  src={firstShipment?.country?.flag || fallbackFlagImageUrl}
                  width={14}
                  alt="shipment-status-transition-modal-flag"
                />
                <span>|</span>
                <span>{`Envío ${optionalShipmentTitle?.current}`}</span>
              </>
            )}
            {isBulkAction && (
              <>
                <span>|</span>
                <span>{`${shipmentsToUse?.length} envíos seleccionados`}</span>
              </>
            )}
          </div>
          <div className="containerDetails">
            {requiresDeliveryDate && (
              <div className="containerDate">
                <NudosDatePicker
                  componentSize="small"
                  label="Fecha de entrega (obligatorio)"
                  defaultValue={deliveryDate}
                  updateCurrentValueCallback={setDeliveryDate}
                  isFilled={false}
                  customLabel="customDeliveryDateLabel"
                  desiredTimeZone={countryTimeZone}
                />
              </div>
            )}
            {requiresTransitTrackingInfo && (
              <div className="trackingFieldsContainer">
                <NudosDatePicker
                  componentSize="small"
                  label='Fecha estimada de ingreso a bodega (obligatorio)'
                  defaultValue={estimatedEntryDate}
                  updateCurrentValueCallback={setEstimatedEntryDate}
                  isFilled={!!estimatedEntryDate}
                  customLabel="customTrackingLabel"
                  desiredTimeZone={countryTimeZone}
                />
                <NudosTextInput
                  label="Código/guía de rastreo (opcional)"
                  isFilled={!!trackingNumber}
                  placeholder="Escribe el código/guía aquí"
                  defaultValue={trackingNumber}
                  styleLabel="customTrackingLabel"
                  handleChange={setTrackingNumber}
                  componentSize="small"
                />
                <NudosTextInput
                  label="Link para tracking del servicio (opcional)"
                  isFilled={!!trackingLink}
                  defaultValue={trackingLink}
                  placeholder="Pega el link"
                  styleLabel="customTrackingLabel"
                  handleChange={setTrackingLink}
                  componentSize="large"
                />
              </div>
            )}
            <div className="containerProducts">
              {transitionConfig.targetStatusId === SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE && (
                <div className="trackingInfoAlertContainer">
                  <NudosStaticAlertInComponent
                    type="information"
                    color="blue"
                    customClassname="shipmentStatusTransitionTrackingAlert"
                    alertOptions={{
                      subtitle:
                        'La fecha estimada de llegada, la guía de rastreo y el seguimiento estarán visibles en Dash una vez que se completen los datos.'
                    }}
                  />
                </div>
              )}
              <ProductSelector
                headerTitle={transitionConfig.headerTitle}
                blockReservedStockSelection={
                  transitionConfig.targetStatusId === SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE
                }
                {...(isLegacyMode
                  ? { shippingChosenByDefault: shipmentsToUse[0] }
                  : { listOfSelectedShipments: shipmentsToUse })}
                listOfShipmentsAvailableToAdd={listOfShipmentsAvailableToAdd}
                setListOfShipmentsToChangeStatus={setListOfShipmentsToChangeStatus}
                displayTemporaryStorageAlert={!!transitionConfig.customAlertMessage}
                customAlertMessage={transitionConfig.customAlertMessage}
                hasTemporaryStorage={false}
                errorList={errorList}
                setErrorList={setErrorList}
                serialHasAnyError={hasErrors => setHasProductSelectorErrors(hasErrors)}
                requireSerials={transitionConfig.requiresSerials ?? false}
                references={references}
                shipmentNumberForLabel={optionalShipmentTitle?.current}
              />
            </div>
          </div>
          <div className="buttonContainer">
            <NudosButton
              componentSize="large"
              colorPalette="primary"
              buttonText={transitionConfig.primaryButtonText}
              isButtonDisabled={disableButton()}
              handleClick={handlePrimaryButtonClick}
              isButtonLoading={loading}
            />
          </div>
        </div>
      </div>
      {showDeliveryFinalConfirm && secondStepVerificationText && (
        <DoubleConfirmationModal
          title="Entrega de productos"
          companyNameInfo={{
            companyName: organizationName || 'Nombre Empresa',
            orderId: Number(orderId) || 0,
            destination: destinationName,
            countryFlag: firstShipment?.country?.flag
          }}
          verificationText={secondStepVerificationText}
          loadingConfirmation={loading}
          handleClose={() => setShowDeliveryFinalConfirm(false)}
          handleConfirmationText={handleConfirmDeliveryFinal}
        />
      )}
    </div>
  );
};

export default ShipmentStatusTransitionModal;
